import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import RedemptionCard from '../components/card/RedemptionCard';
import Navbar from '../components/navbar/Navbar';
import Footer from '../components/footer/Footer';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { fetchVoucher } from '../redux/voucherActions';
import { clearError } from '../redux/voucherReducer';
import ErrorPopup from '../components/ErrorPopup/ErrorPopup';

const loadingOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(8px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
};

function HomePage(): JSX.Element {
  const { code: codeParam } = useParams<{ code?: string }>();
  const [searchParams] = useSearchParams();
  // Get code from URL path parameter first, then fall back to query parameter
  const codeFromPath = codeParam ? codeParam.toUpperCase().replace(/[^A-Z0-9-]/g, "") : "";
  const codeFromQuery = searchParams.get("code") ? searchParams.get("code")!.toUpperCase().replace(/[^A-Z0-9-]/g, "") : "";
  const code = codeFromPath || codeFromQuery;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error } = useAppSelector((state: any) => state.voucher);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  
  // Removed auto-redirect on voucher to avoid back->push loop. We navigate after fetch instead.

  useEffect(() => {
    if (error) {
      setShowErrorPopup(true);
    }
  }, [error]);

  const handlePopupClose = (): void => {
    setShowErrorPopup(false);
    dispatch(clearError());
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    try {
      const formData = new FormData(e.currentTarget);
      const codeValue = formData.get('code') as string;
      const pinValue = formData.get('pin') as string;
      await dispatch(fetchVoucher({ saral_code: codeValue, pin: pinValue })).unwrap();
      navigate('/create-voucher', { replace: true });
    } catch (error) {
      // Error is handled by Redux and displayed in ErrorPopup
      // No need to log here as it's already logged in voucherActions
    }
  };
  
  

  return (
    <div className="min-h-screen" style={{ 
      backgroundImage: 'url(https://saraluat.blr1.cdn.digitaloceanspaces.com/redemption_portal/gingham%20bg.png), linear-gradient(180deg, rgba(221, 236, 235, 1) 55%, rgba(221, 236, 235, 0) 100%)', 
      backgroundSize: 'cover'
    }}>
      {loading && (
        <div className="loading-overlay" style={loadingOverlayStyle}>
          <div className="flex flex-col items-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#53978e] border-t-transparent"></div>
            <p className="text-gray-600 font-medium">Processing your request...</p>
          </div>
        </div>
      )}

      <div >
        <Navbar />
        <main className="container mx-auto px-4 md:px-0  py-6 sm:py-8 flex flex-col" >
          {/* Hero and Form Section - Side by Side */}
          <section className=" flex flex-col lg:flex-row items-center justify-between gap-8  flex-1">
            {/* Hero Section */}
            <div className="w-full lg:w-1/2  lg:mt-0">
              <div className="leading-tight text-center lg:text-left font-bold  pb-5  bg-[linear-gradient(100deg,rgba(27,49,46,1)_0%,rgba(83,151,142,1)_100%)] [-webkit-background-clip:text] bg-clip-text [-webkit-text-fill-color:transparent] [text-fill-color:transparent] [font-family:'Inter',Helvetica] font-semibold text-transparent md:text-[79px] text-[50px]">

                Welcome to <br />
                Fincentive Pilot <br />
                Programme
              </div>

              <p className="text-center lg:text-left font-medium text-[#1c1c1c] md:text-[28px] text-[20px] ">
                Enter your voucher code &amp; PIN <br />
                to redeem or check your balance
              </p>

              {/* New Help and Sales Section - Hidden on mobile, shown on desktop */}
              <section className="hidden lg:grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
                <div className="flex flex-col items-start text-left">
                  <img className="w-6 h-6 mb-2" alt="Call center help" src="https://saraluat.blr1.cdn.digitaloceanspaces.com/redemption_portal/help%20logo.png" />
                  <p className="font-medium text-md mt-0">Need Help?</p>
                  <p className="text-gray-600 text-sm flex-grow">
                    Explore our comprehensive Help Center for detailed user guides and answers to frequently asked questions about voucher redemption.
                  </p>
                  <p className="bg-gradient-to-r font-medium from-[#1b312e] to-[#53978e] text-transparent bg-clip-text text-lg mt-2 flex items-center whitespace-nowrap">
                    Visit Help Center
                    <img className="w-3 h-3 ml-1" alt="Vector" src="https://saraluat.blr1.cdn.digitaloceanspaces.com/redemption_portal/link%20logo.png" />
                  </p>
                </div>
                <div className="flex flex-col items-start text-left">
                  <img className="h-6 mb-2" alt="Vector" src="https://saraluat.blr1.cdn.digitaloceanspaces.com/redemption_portal/email%20logo.png" />
                  <p className="font-medium text-md mt-0">Contact Sales Team</p>
                  <p className="text-gray-600 text-sm flex-grow">
                    Our dedicated sales team is ready to assist you with getting started or finding the perfect solutions for your business needs.
                  </p>
                  <p className="bg-gradient-to-r font-medium from-[#1b312e] to-[#53978e] text-transparent bg-clip-text text-lg mt-2 flex items-center whitespace-nowrap">
                    Email us at sales@fincentive.co
                    <img className="w-3 h-3 ml-1" alt="Vector" src="https://saraluat.blr1.cdn.digitaloceanspaces.com/redemption_portal/link%20logo.png" />
                  </p>
                </div>
              </section>






            </div>

            {/* Redemption Form Section */}
            <div className="w-full lg:w-1/2 flex justify-center lg:justify-end items-center px-2 sm:px-0">
              <RedemptionCard onSubmit={handleSubmit} initialCode={code} />
            </div>
          </section>

          {/* Mobile Help and Sales Section - Shown on mobile, hidden on desktop */}
          <section className="lg:hidden grid grid-cols-1 gap-8 mt-8 mx-3 md:px-0">
            <div className="flex flex-col items-start text-left">
              <img className="w-6 h-6 mb-2" alt="Call center help" src="https://saraluat.blr1.cdn.digitaloceanspaces.com/redemption_portal/help%20logo.png" />
              <p className="font-medium text-md mt-0">Need Help?</p>
              <p className="text-gray-600 text-sm flex-grow">
                Explore our comprehensive Help Center for detailed user guides and answers to frequently asked questions about voucher redemption.
              </p>
              <a href="https://fincentive.co/#contact" className="bg-gradient-to-r font-medium from-[#1b312e] to-[#53978e] text-transparent bg-clip-text text-lg mt-2 flex items-center whitespace-nowrap cursor-pointer">
                Visit Help Center
                <img className="w-3 h-3 ml-1" alt="Vector" src="https://saraluat.blr1.cdn.digitaloceanspaces.com/redemption_portal/link%20logo.png" />
              </a>
            </div>
            <div className="flex flex-col items-start text-left">
              <img className=" h-6 mb-2" alt="Vector" src="https://saraluat.blr1.cdn.digitaloceanspaces.com/redemption_portal/email%20logo.png" />
              <p className="font-medium text-md mt-0">Contact Sales Team</p>
              <p className="text-gray-600 text-sm flex-grow">
                Our dedicated sales team is ready to assist you with getting started or finding the perfect solutions for your business needs.
              </p>
              <a href="https://fincentive.co/#contact" className="bg-gradient-to-r font-medium from-[#1b312e] to-[#53978e] text-transparent bg-clip-text text-lg mt-2 flex items-center whitespace-nowrap cursor-pointer">
                Email us at sales@fincentive.co
                <img className="w-3 h-3 ml-1" alt="Vector" src="https://saraluat.blr1.cdn.digitaloceanspaces.com/redemption_portal/link%20logo.png" />
              </a>
            </div>
          </section>

        </main>
      </div>

      {showErrorPopup && (
        <ErrorPopup
          message={error || "An error occurred"}
          onClose={handlePopupClose}
        />
      )}

      <Footer />
    </div>
  );
}

export default HomePage; 